import { buildApiUrl } from "@/lib/appPaths";
import type { AuthUser } from "@/contexts/AuthContext";

type AuthResponse = {
  user?: AuthUser;
  message?: string;
  detail?: string;
  accepted?: boolean;
  valid?: boolean;
  reset?: boolean;
  invite_reward?: {
    credited?: boolean;
    amount?: number;
  } | null;
  profile?: {
    professorName?: string;
    school?: string;
    college?: string;
  };
};

function resolveErrorMessage(body: unknown, fallback: string) {
  if (body && typeof body === "object") {
    const payload = body as { detail?: string | { msg?: string; code?: string }; message?: string };
    if (typeof payload.detail === "string" && payload.detail.trim()) {
      return payload.detail;
    }
    if (typeof payload.detail === "object" && payload.detail?.msg) {
      return payload.detail.msg;
    }
    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message;
    }
  }
  return fallback;
}

export async function fetchCurrentUser() {
  const response = await fetch(buildApiUrl("/auth/me"), {
    credentials: "include",
  });

  if (!response.ok) {
    return null;
  }

  const payload = (await response.json().catch(() => null)) as AuthResponse | null;
  return payload?.user || null;
}

export async function loginWithPassword(payload: {
  identifier: string;
  password: string;
  rememberMe: boolean;
}) {
  const response = await fetch(buildApiUrl("/auth/login"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  const body = (await response.json().catch(() => null)) as AuthResponse | null;

  return {
    ok: response.ok,
    status: response.status,
    user: body?.user || null,
    message: resolveErrorMessage(body, "登录失败，请检查账号或密码"),
  };
}

export async function logoutCurrentUser() {
  const response = await fetch(buildApiUrl("/auth/logout"), {
    method: "POST",
    credentials: "include",
  });

  return response.ok;
}

export async function sendEmailVerificationCode(email: string) {
  const response = await fetch(buildApiUrl("/auth/send-verification-code"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ email }),
  });

  const body = (await response.json().catch(() => null)) as { message?: string; detail?: string } | null;

  return {
    ok: response.ok,
    status: response.status,
    message: resolveErrorMessage(body, "发送失败，请稍后重试"),
  };
}

export async function verifyEmailCode(email: string, code: string) {
  const response = await fetch(buildApiUrl("/auth/verify-email-code"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ email, code }),
  });

  const body = (await response.json().catch(() => null)) as { verification_token?: string; message?: string; detail?: string } | null;

  return {
    ok: response.ok,
    status: response.status,
    verificationToken: body?.verification_token || null,
    message: resolveErrorMessage(body, "验证失败，请重试"),
  };
}

export async function signUpWithPassword(payload: {
  email: string;
  username: string;
  password: string;
  fullName?: string;
  verificationToken: string;
  inviteCode?: string;
  organizationCode?: string;
  studentNumber?: string;
}) {
  const response = await fetch(buildApiUrl("/auth/signup"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      email: payload.email,
      username: payload.username,
      password: payload.password,
      fullName: payload.fullName,
      verification_token: payload.verificationToken,
      invite_code: payload.inviteCode,
      organization_code: payload.organizationCode,
      student_number: payload.studentNumber,
    }),
  });

  const body = (await response.json().catch(() => null)) as AuthResponse | null;

  return {
    ok: response.ok,
    status: response.status,
    user: body?.user || null,
    inviteReward: body?.invite_reward || null,
    message: resolveErrorMessage(body, "注册失败，请稍后重试"),
  };
}

export async function requestPasswordReset(email: string) {
  const response = await fetch(buildApiUrl("/auth/forgot-password"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ email }),
  });

  const body = (await response.json().catch(() => null)) as AuthResponse | null;

  return {
    ok: response.ok,
    status: response.status,
    accepted: Boolean(body?.accepted),
    message: body?.message,
  };
}

export async function validatePasswordResetToken(token: string) {
  const response = await fetch(buildApiUrl("/auth/validate-reset-token"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ token }),
  });

  const body = (await response.json().catch(() => null)) as AuthResponse | null;

  return {
    ok: response.ok,
    status: response.status,
    user: body?.user || null,
    valid: Boolean(body?.valid),
    message: body?.message,
  };
}

export async function resetPasswordWithToken(payload: {
  token: string;
  newPassword: string;
}) {
  const response = await fetch(buildApiUrl("/auth/reset-password"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      token: payload.token,
      new_password: payload.newPassword,
    }),
  });

  const body = (await response.json().catch(() => null)) as AuthResponse | null;

  return {
    ok: response.ok,
    status: response.status,
    reset: Boolean(body?.reset),
    user: body?.user || null,
    message: body?.message,
  };
}

export async function updateCurrentUserProfile(payload: {
  professorName: string;
  school: string;
  college: string;
}) {
  const response = await fetch(buildApiUrl("/auth/profile"), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      professor_name: payload.professorName,
      school: payload.school,
      college: payload.college,
    }),
  });

  const body = (await response.json().catch(() => null)) as AuthResponse | { detail?: string } | null;

  return {
    ok: response.ok,
    status: response.status,
    user: "user" in (body || {}) ? (body as AuthResponse).user || null : null,
    profile: "profile" in (body || {}) ? (body as AuthResponse).profile || null : null,
    message: resolveErrorMessage(body, "更新个人资料失败"),
  };
}

export async function uploadCurrentUserAvatar(file: File) {
  const formData = new FormData();
  formData.set("file", file);

  const response = await fetch(buildApiUrl("/auth/profile/avatar"), {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  const body = (await response.json().catch(() => null)) as AuthResponse | { detail?: string } | null;

  return {
    ok: response.ok,
    status: response.status,
    user: "user" in (body || {}) ? (body as AuthResponse).user || null : null,
    message: resolveErrorMessage(body, "上传头像失败"),
  };
}
